<template>
	<v-container v-if="country && country.gdp.length > 0" class="">
		<v-row>
			<v-col cols="12" md="6" class="pl-0">
				<v-row class="countryNameBar__holder pb-8 pl-3 ma-0">
					<country-name-bar :countryName="country.country_name" />
				</v-row>
				<div class="pa-0 ma-0">
					<v-tabs
						v-model="activeFigure"
						slider-color="#DC0028"
						class="keyFiguresTabsMobile"
						:show-arrows="false"
					>
						<v-tab
							v-for="(figure, index) in keyFigures"
							:key="figure.title"
							@click="setActiveFigure(index)"
						>
							{{ figure.title }}
						</v-tab>
					</v-tabs>
				</div>
				<div class="figuresList">
					<div
						v-for="(figure, index) in keyFigures"
						:key="figure.title"
						:class="['figureTitle', { active: index === activeFigure }]"
						@click="setActiveFigure(index)"
						class="mb-6"
					>
						{{ figure.title }}
					</div>
				</div>
			</v-col>
			<v-col class="mobileText pa-0 ma-0" cols="12">
			</v-col>

			<v-col cols="12" md="6" class="pa-0 ma-0">
				<v-row class="chartContainer pa-0 ma-0">
					<svg ref="chartCanvas" class="chartCanvas" preserveAspectRatio="xMinYMin meet"></svg>
				</v-row>
				<v-row class="chartInfoRow">
					<span class="sourcesText">Source: Macrobond</span>
					<div v-if="keyFigures[activeFigure].key === 'importData'" class="legend">
						<div class="legend-item">
							<svg width="12" height="2">
								<rect width="12" height="2" fill="#DC0028"></rect>
							</svg>
							<span>Imports</span>
						</div>
						<div class="legend-item">
							<svg width="12" height="2">
								<rect width="12" height="2" fill="#333333"></rect>
							</svg>
							<span>Exports</span>
						</div>
					</div>
				</v-row>
			</v-col>
		</v-row>
		<div id="tooltip" style="position: absolute; opacity: 0; background-color: white; border: 1px solid black; padding: 5px; pointer-events: none;"></div>
	</v-container>
</template>


<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted } from 'vue';
import store from '@/store';
import * as d3 from 'd3';

export default defineComponent({
	name: 'KeyFiguresComponent',
    setup: function() {
		
		const isMobile = ref(window.innerWidth <= 900);

		const country = computed({
			get() {
				return store.getters['countryStoreData/selectedCountry'];
			},
			set(value) {
				store.dispatch('stateStoreData/setCountry', value);
			}
		});

		const keyFigures = ref([
			{ title: 'Real GDP', key: 'gdp' },
			{ title: 'Inflation', key: 'inflation' },
			{ title: 'Real private consumption', key: 'consumption' },
			{ title: 'Real private investment', key: 'investment' },
			{ title: 'Fiscal balance', key: 'cpi' },
			{ title: 'Imports/Exports', key: 'importData' }
		]);

		const chartTitle = ref([
			{ title: 'GDP', subtitle: "% year-on-year change", key: 'gdp' },
			{ title: 'Inflation', subtitle: "% year-on-year change", key: 'inflation' },
			{ title: 'Real private consumption', subtitle: "% year-on-year change", key: 'consumption' },
			{ title: 'Real private investment', subtitle: "% year-on-year change", key: 'investment' },
			{ title: 'Fiscal balance', subtitle: "% of GDP", key: 'cpi' },
			{ title: 'Imports/Exports', subtitle: "% year-on-year change", key: 'importData' }
		]);

		const activeFigure = ref(0);
		const chartCanvas = ref<SVGSVGElement | null>(null);
		
		const updateChart = () => {

			if (!chartCanvas.value || !country.value) return false;

			const figureKey = keyFigures.value[activeFigure.value].key;
			const isImportExport = figureKey === 'importData';
			// Adjusting figureData mapping based on the key
			const figureData: { year: string; pct_change: number; value?: number }[] = country.value[figureKey];
		
			const labels = figureData.map((item: any) => item.year);
			// Use item.value if figureKey is 'cpi', otherwise use item.pct_change
			const data = figureData.map((item: any) => figureKey === 'cpi' ? parseFloat(item.value) : parseFloat(item.pct_change));

			const exportData: { year: string; pct_change: number; value?: number }[] = isImportExport ? country.value['exportData'] : [];
			const exportValues = isImportExport ? exportData.map((item: any) => parseFloat(item.pct_change)) : [];

			const margin = { top: 84, right: 80, bottom: 50, left: 30 };
			if (!chartCanvas.value.parentElement) return false;
			const containerWidth = chartCanvas.value.parentElement.clientWidth;
			const containerHeight = chartCanvas.value.parentElement.clientHeight;
			const width = containerWidth - margin.left - margin.right;
			const height = containerHeight - margin.top - margin.bottom;

			d3.select(chartCanvas.value).selectAll('*').remove();

			const svg = d3.select(chartCanvas.value)
				.attr('width', '100%')
				.attr('height', '100%')
				.attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
				.append('g')
				.attr('transform', `translate(${margin.left},${margin.top})`);

			svg.append('text')
				.attr('x', 0)
				.attr('y', -35)
				.attr('text-anchor', 'start')
				.style('font-size', '16px')
				.style('font-weight', '700')
				.style('font-family', 'GCO Sans')
				.text(chartTitle.value[activeFigure.value].title);




			if (figureData.length == 0)
			{
				svg.append('text')
				.attr('x', 0)
				.attr('y', -15)
				.attr('text-anchor', 'start')
				.style('font-size', '16px')
				.style('font-weight', '400')
				.style('font-family', 'GCO Sans')
				.text('No data available');

				return false;
			}


			svg.append('text')
				.attr('x', 0)
				.attr('y', -15)
				.attr('text-anchor', 'start')
				.style('font-size', '16px')
				.style('font-weight', '400')
				.style('font-family', 'GCO Sans')
				.text(chartTitle.value[activeFigure.value].subtitle);

			const x = d3.scalePoint()
				.domain(labels)
				.range([0, width])
				.padding(0);

				const yDomain: [number, number] = [
					Math.min(-20, d3.min(data, d => d as number) ?? 0, d3.min(exportValues, d => d as number) ?? 0),
					Math.max(20, d3.max(data, d => d as number) ?? 2, d3.max(exportValues, d => d as number) ?? 2)
				];

			const y = d3.scaleLinear()
				.domain(yDomain)
				.nice()
				.range([(height -20), 0]);

			svg.append('g')
				.attr('transform', `translate(0,${height})`)
				.call(d3.axisBottom(x).ticks(labels.length).tickFormat((d, i) => labels[i]))
				.call(g => g.select('.domain').remove())
				.call(g => g.selectAll('.tick line').remove())
				.selectAll('text')
				.style('font-size', '12px');

			const yAxis = svg.append('g')
				.attr('transform', `translate(${width},0)`)
				.call(d3.axisRight(y).ticks(6))
				.call(g => g.select('.domain').remove())
				.call(g => g.selectAll('.tick line').remove());

			yAxis.selectAll('text')
				.style('font-size', '12px');

			svg.append('g')
				.attr('class', 'grid')
				.call(d3.axisLeft(y).ticks(6)
					.tickSize(-width)
					.tickFormat(() => ''))
				.call(g => g.select('.domain').remove())
				.selectAll('.tick line')
				.style('stroke', (d) => d === 0 ? '#A5A5A5' : '#e0e0e0')
				.style('stroke-width', (d) => d === 0 ? '1.5px' : '1px');

			const line = d3.line<number>()
				.x((d, i) => x(labels[i])!)
				.y(d => y(d));

			const importPath = svg.append('path')
				.datum(data)
				.attr('fill', 'none')
				.attr('stroke', '#DC0028')
				.attr('stroke-width', 2)
				.attr('d', line);

			const totalLengthImport = (importPath.node() as SVGPathElement).getTotalLength();

			importPath
				.attr('stroke-dasharray', `${totalLengthImport} ${totalLengthImport}`)
				.attr('stroke-dashoffset', totalLengthImport)
				.transition()
				.duration(2000)
				.ease(d3.easeLinear)
				.attr('stroke-dashoffset', 0);

			if (isImportExport) {
				const exportPath = svg.append('path')
					.datum(exportValues)
					.attr('fill', 'none')
					.attr('stroke', '#333333')
					.attr('stroke-width', 2)
					.attr('d', line);

				const totalLengthExport = (exportPath.node() as SVGPathElement).getTotalLength();

				exportPath
					.attr('stroke-dasharray', `${totalLengthExport} ${totalLengthExport}`)
					.attr('stroke-dashoffset', totalLengthExport)
					.transition()
					.duration(2000)
					.ease(d3.easeLinear)
					.attr('stroke-dashoffset', 0);
			}

			// Tooltip
			const tooltip = d3.select('body')
				.append('div')
				.attr('class', 'tooltip')
				.style('position', 'absolute')
				.style('background', '#fff')
				.style('border', '1px solid #ccc')
				.style('padding', '10px')
				.style('pointer-events', 'none')
				.style('opacity', 0)
				.style('z-index', 10000); // Added z-index


			const circles = svg.selectAll('circle')
				.data(data)
				.enter()
				.append('circle')
				.attr('cx', (d, i) => x(labels[i])!)
				.attr('cy', d => y(d as number))
				.attr('r', 4)
				.attr('fill', '#DC0028')
				.attr('stroke', '#fff')
				.attr('stroke-width', 0)
				.on('mouseover', (event, d) => {
					tooltip.style('opacity', 1);
					d3.select(event.target).attr('r', 6);
				})
				.on('mousemove', (event, d) => {
						const roundedValue = d.toFixed(1);
						tooltip.html(`Year: ${labels[data.indexOf(d)]}<br>Value: ${roundedValue}%`)
								.style('left', `${event.pageX + 5}px`)
								.style('top', `${event.pageY - 28}px`);
				})
				.on('mouseout', (event, d) => {
					tooltip.style('opacity', 0);
					d3.select(event.target).attr('r', 4);
				});

			if (isImportExport) {
				const exportCircles = svg.selectAll('circle.export')
					.data(exportValues)
					.enter()
					.append('circle')
					.attr('class', 'export')
					.attr('cx', (d, i) => x(labels[i])!)
					.attr('cy', d => y(d as number))
					.attr('r', 4)
					.attr('fill', '#333333')
					.attr('stroke', '#fff')
					.attr('stroke-width', 0)
					.on('mouseover', (event, d) => {
						tooltip.style('opacity', 1);
						d3.select(event.target).attr('r', 6);
					})
					.on('mousemove', (event, d) => {
						tooltip.html(`Year: ${labels[exportValues.indexOf(d)]}<br>Value: ${(d).toFixed(1)}%`)
							.style('left', `${event.pageX + 5}px`)
							.style('top', `${event.pageY - 28}px`);
					})
					.on('mouseout', (event, d) => {
						tooltip.style('opacity', 0);
						d3.select(event.target).attr('r', 4);
					});
			}
			
			return true;
		};

		watch(
			() => activeFigure.value,
			() => {
				updateChart();
			}
		);

		watch(
			() => country.value,
			() => {
				updateChart();
			},
			{ immediate: true }
		);

		onMounted(() => {
				updateChart();
		});

		const setActiveFigure = (index: number) => {
			activeFigure.value = index;
		};
		
		return {
			country,
			keyFigures,
			activeFigure,
			chartCanvas,
			setActiveFigure
		};
	},
});
</script>

<style scoped>
.tooltip {
	background: #fff;
	border: 1px solid #ccc;
	padding: 10px;
	pointer-events: none;
	opacity: 0;
	transition: opacity 0.2s;
	z-index: 10000; /* Ensure tooltip is on top */
}

.mobileText {
	display: none;
}
.mobileText > p {
	color: #606A76;
	margin-top: 2em;
	margin-bottom: 2em;
}
.chartContainer {
	width: 100%;
	height: 95%;
	background-color: white;
	padding: 20px 20px 0 20px;
	border-radius: 8px;
}
.sourcesText {
	font-family: 'GCO Sans', sans-serif;
	font-size: 12px;
	font-weight: 400;
	line-height: 16px;
	color: #606A76;
	margin-top: 10px;
	margin-left: 26px;
	height: 5%;
}
.figuresList {
	display: flex;
	flex-direction: column;
	padding: 0;
}
.figureTitle {
	font-family: 'GCO', sans-serif;
	font-size: 20px;
	font-weight: 500;
	line-height: 26px;
	letter-spacing: -0.25px;
	margin-bottom: 10px;
	cursor: pointer;
	color: #333;
}
.figureTitle.active {
	font-weight: 500;
	color: #DC0028;
}
.separator {
	height: 1px;
	background-color: #bebebe;
	margin: 16px 26px 0px 0px;
}
.chartCanvas {
	height: 100%;
	width: 100%;
}
.keyFiguresTabsMobile {
	display: none;
}
.keyFiguresTabsMobile .v-btn__content {
	font-weight: 400;
	letter-spacing: 0.48px;
}
.legend {
	display: flex;
	flex-direction: row;
	align-items: flex-end;
	margin-right: 30px;
}
.legend-item svg {
	margin-right: 4px;
	margin-left: 8px;
	height: 2px;
}
.legend-item rect {
	width: 12px;
	height: 2px;
}
.legend-item {
	display: flex;
	align-items: center;
}
.legend-item span {
	font-size: 12px;
	font-family: 'GCO Sans', sans-serif;
}
.chartInfoRow {
	display: flex;
	justify-content: space-between;
}
@media (max-width: 900px) {
	.mobileText {
		display: block;
		padding: 0 20px;
		font-size: 16px;
		font-weight: 400;
		line-height: 24px;
		color: #000000;
	}
	.figuresList {
		display: none;
	}
	.keyFiguresTabsMobile {
		display: flex;
		justify-content: space-between;
	}
	.chartContainer {
		height: 300px;
	}
}
@media print {
	.contentRow * {
		visibility: hidden;
	}
	.chartContainer * {
		visibility: visible !important;
		position: absolute;
		top: 10%;
		left: 10%;
		height: 60%;
	}
}
</style>


<style>
	.esgTabsMobile .v-slide-group__prev,
	.esgTabsMobile .v-slide-group__next {
		display: none !important;
	}
	
	.keyFiguresTabsMobile .v-btn__content{
		font-weight: 500;
		text-transform: capitalize !important;	
		letter-spacing: 0.48px;
	}
	#tooltip {
		position: fixed; /* Change this from absolute to fixed */
		pointer-events: none;
		background-color: rgba(255, 255, 255, 0.9);
		border: 1px solid #ccc;
		border-radius: 4px;
		padding: 8px;
		font-size: 12px;
		font-family: 'GCO Sans', sans-serif;
		box-shadow: 0 2px 4px rgba(0,0,0,0.1);
		z-index: 1000;
		transition: opacity 0.2s;
	}


	.keyFiguresTabsMobile span{
		font-family: 'GCO Sans', sans-serif;
		color: #333;
		font-size: 16px;
		font-weight: 500 !important;
	}
</style>